/* eslint-disable max-len */
/**
 * Data for the in-app guided tour.
 *
 * The tour is organised into named *tracks*. `welcome` is the short, role-adaptive
 * walkthrough that auto-shows once on first workspace selection; the remaining
 * tracks are opt-in deep-dives replayed from the Help center, each mapping to a
 * feature area. Keeping this as plain data means wording and ordering can be
 * edited without touching the overlay/positioning logic in index.jsx.
 *
 * Each step:
 *   id            unique key (within its track)
 *   route         pathname the tour navigates to before showing the step
 *   minLevel      lowest role level that legitimately has this screen — steps
 *                 above the member's level are dropped so the tour never routes
 *                 a non-admin into an admin-only screen (which would 403 on its
 *                 data calls). Levels: Team member 1, Team Leader 2,
 *                 Workspace admin 3, Workspace owner 4, Primary owner 5.
 *   selector      element to spotlight; null renders a centered modal instead
 *   placement     popover side relative to the target: top|bottom|left|right|center
 *   optional      if true, the step is skipped silently when its anchor is absent
 *                 (for conditionally-rendered UI, e.g. the admin-only Create Team)
 *   title, body   copy
 */
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';

// Force-start a tour. detail.tour selects the track (defaults to 'welcome').
export const TOUR_START_EVENT = 'shoot:start-tour';
// Check backend status and start the welcome tour only if not yet seen
// (dispatched after workspace selection, with { detail: { workspaceId } }).
export const TOUR_CHECK_EVENT = 'shoot:tour-check';

export const WELCOME_TOUR_ID = 'welcome';

// Below this role level the tour (about managing members, teams and reports)
// isn't relevant, so it neither auto-shows nor offers a replay.
const MIN_TOUR_LEVEL = accountTypeLevel['Team Leader']; // 2
const ADMIN_LEVEL = accountTypeLevel['Workspace admin']; // 3
const OWNER_LEVEL = accountTypeLevel['Workspace owner']; // 4

/**
 * All tours keyed by track id. The first key (`welcome`) is the auto-shown tour.
 */
export const TOURS = {
  welcome: [
    {
      id: 'welcome',
      route: '/dashboard',
      minLevel: MIN_TOUR_LEVEL,
      selector: null,
      placement: 'center',
      title: 'Welcome to Shoot 👋',
      body: 'Take a quick tour of the essentials — your sidebar, dashboard, teams, timesheets and time off. It only takes a minute, and you can skip out at any time.',
    },
    {
      id: 'nav',
      route: '/dashboard',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="nav"]',
      placement: 'right',
      title: 'Your sidebar',
      body: 'Everything lives here. The menu adapts to your role — jump between your dashboard, teams, reports and settings from this list.',
    },
    {
      id: 'dashboard',
      route: '/dashboard',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="dashboard"]',
      placement: 'bottom',
      title: 'Shoot Insights',
      body: 'Your home base. Engagement, activity and critical alerts at a glance.',
    },
    {
      id: 'dashboard-view',
      route: '/dashboard',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="dashboard-view"]',
      placement: 'left',
      title: 'Two ways to read it',
      body: 'Flip this switch to swap between the visual insight cards and a detailed tabular view of the same data.',
    },
    {
      id: 'team-engagement',
      route: '/members/team-engagement',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="team-kpis"]',
      placement: 'bottom',
      title: 'Team engagement',
      body: 'Group members under leaders and watch each team’s engagement score. The KPIs up top flag how many teams are at risk so you know where to look first.',
    },
    {
      id: 'team-buckets',
      route: '/members/team-engagement',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="team-buckets"]',
      placement: 'bottom',
      title: 'Filter by health',
      body: 'Narrow the list to teams that are thriving, steady or at risk — or search by name — to focus on what needs attention.',
    },
    {
      id: 'members',
      route: '/members/manage',
      minLevel: ADMIN_LEVEL,
      selector: '[data-testid="new-member"]',
      placement: 'bottom',
      title: 'Manage members',
      body: 'Invite people one at a time with New Member, or bring in many at once with Import Users. Each row’s “···” menu holds every per-user action.',
    },
    {
      id: 'timesheets',
      route: '/members/timesheets',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="timesheets"]',
      placement: 'bottom',
      title: 'Timesheets',
      body: 'Review tracked hours per person and export them for payroll or billing in a couple of clicks.',
    },
    {
      id: 'profile-requests',
      route: '/profile-update-requests',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="profile-requests"]',
      placement: 'bottom',
      title: 'Profile update requests',
      body: 'When a team member asks to change their profile details, the request lands here for you to review, approve or reject.',
    },
    {
      id: 'timeoff',
      route: '/pto/requests',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="pto"]',
      placement: 'bottom',
      title: 'Time off',
      body: 'Request your own time off and track its status here. Managers can also review their team’s requests.',
    },
    {
      id: 'help',
      route: '/help',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="nav-help"]',
      placement: 'right',
      title: 'You’re all set',
      body: 'Full guides — and deeper tours of each feature area — live under Help & Docs. Replay any of them anytime.',
    },
  ],

  teams: [
    {
      id: 'team-kpis',
      route: '/members/team-engagement',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="team-kpis"]',
      placement: 'bottom',
      title: 'Engagement at a glance',
      body: 'These KPIs summarise how many teams are thriving versus at risk across the whole workspace.',
    },
    {
      id: 'team-buckets',
      route: '/members/team-engagement',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="team-buckets"]',
      placement: 'bottom',
      title: 'Filter & search teams',
      body: 'Filter by engagement health or search by name to jump straight to the team you care about.',
    },
    {
      id: 'create-team',
      route: '/members/team-engagement',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="create-team"]',
      placement: 'left',
      optional: true,
      title: 'Create a team',
      body: 'Spin up a new team, give it a leader, and start assigning members to it.',
    },
    {
      id: 'team-pto',
      route: '/pto/members',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="team-pto"]',
      placement: 'bottom',
      title: 'Your team’s time off',
      body: 'See upcoming and pending time-off requests for the people who report to you, and act on them here.',
    },
    {
      id: 'org-chart',
      route: '/organization-chart',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="org-chart"]',
      placement: 'bottom',
      title: 'Organization chart',
      body: 'Visualise the full reporting structure of your workspace — who reports to whom, top to bottom.',
    },
  ],

  members: [
    {
      id: 'members-intro',
      route: '/members/manage',
      minLevel: ADMIN_LEVEL,
      selector: '[data-testid="new-member"]',
      placement: 'bottom',
      title: 'Add a member',
      body: 'Invite someone by email and set their role, reporting manager, department and designation up front.',
    },
    {
      id: 'members-import',
      route: '/members/manage',
      minLevel: ADMIN_LEVEL,
      selector: '[data-testid="import-users"]',
      placement: 'bottom',
      optional: true,
      title: 'Bulk import',
      body: 'Onboarding lots of people? Import Users lets you bring in many at once from a spreadsheet.',
    },
    {
      id: 'members-filter',
      route: '/members/manage',
      minLevel: ADMIN_LEVEL,
      selector: '[data-testid="status-filter"]',
      placement: 'bottom',
      optional: true,
      title: 'Find people fast',
      body: 'Filter by status — active, invited or deactivated — or search to locate any member instantly.',
    },
  ],

  reporting: [
    {
      id: 'timesheets',
      route: '/members/timesheets',
      minLevel: MIN_TOUR_LEVEL,
      selector: '[data-tour="timesheets"]',
      placement: 'bottom',
      title: 'Timesheets',
      body: 'Per-person tracked hours, ready to review and export for payroll or billing.',
    },
    {
      id: 'report-kpis',
      route: '/reports/time-worked',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="report-kpis"]',
      placement: 'bottom',
      title: 'Time Worked report',
      body: 'Total, manual and quality hours plus an engagement score — the headline numbers for any range.',
    },
    {
      id: 'report-filters',
      route: '/reports/time-worked',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="report-filters"]',
      placement: 'bottom',
      optional: true,
      title: 'Slice the data',
      body: 'Pick a date range and filter down to a single team or member to answer a specific question.',
    },
    {
      id: 'report-export',
      route: '/reports/time-worked',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="report-export"]',
      placement: 'left',
      optional: true,
      title: 'Export it',
      body: 'Download the activity table as a CSV to share or analyse outside Shoot.',
    },
    {
      id: 'unusual-activity',
      route: '/analytics/unusual-activity',
      minLevel: OWNER_LEVEL,
      selector: '[data-tour="unusual-activity"]',
      placement: 'bottom',
      optional: true,
      title: 'Unusual activity',
      body: 'Analytics surfaces anomalies in tracked activity so you can spot patterns that warrant a closer look.',
    },
  ],

  monitoring: [
    {
      id: 'settings',
      route: '/admin/settings',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="settings"]',
      placement: 'bottom',
      title: 'Settings & permissions',
      body: 'Workspace-wide controls — tracking, privacy and policy — all live under Settings.',
    },
    {
      id: 'screenshots',
      route: '/members/screenshotFrequency',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="screenshots"]',
      placement: 'bottom',
      title: 'Screenshot frequency',
      body: 'Tune how often (and whether) screenshots are captured, per your team’s monitoring policy.',
    },
    {
      id: 'webcam-audit',
      route: '/members/webcamAudit',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="webcam-audit"]',
      placement: 'bottom',
      title: 'Webcam audit',
      body: 'Configure periodic webcam checks used for identity assurance during tracked sessions.',
    },
    {
      id: 'insider-threats',
      route: '/insiderThreats',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="insider-threats"]',
      placement: 'bottom',
      optional: true,
      title: 'Insider threats',
      body: 'Flags risky behaviour patterns so you can investigate potential insider-threat signals early.',
    },
    {
      id: 'geo',
      route: '/geoDashboard',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="geo"]',
      placement: 'bottom',
      optional: true,
      title: 'Geolocation',
      body: 'Track location compliance against each member’s registered work address and review violations.',
    },
  ],

  roles: [
    {
      id: 'role-management',
      route: '/role-management',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="role-management"]',
      placement: 'bottom',
      title: 'Roles & permissions',
      body: 'Define custom roles, choose exactly what each can see and do, and assign them to members.',
    },
    {
      id: 'activity-logs',
      route: '/activity-logs',
      minLevel: ADMIN_LEVEL,
      selector: '[data-tour="activity-logs"]',
      placement: 'bottom',
      title: 'Activity logs',
      body: 'A full audit trail of administrative actions in your workspace — who changed what, and when.',
    },
  ],
};

/**
 * Opt-in feature tracks for the Help center, in display order. (Excludes the
 * auto-shown welcome tour.) `minLevel` here is the lowest level with at least
 * one accessible step; `getAccessibleTracks` does the real per-member filtering.
 */
export const TRACKS = [
  {
    id: 'teams', label: 'Teams & structure', description: 'Engagement, team filters and the org chart.', icon: 'Layers', minLevel: MIN_TOUR_LEVEL,
  },
  {
    id: 'members', label: 'Managing members', description: 'Invite, import and find people.', icon: 'Users', minLevel: ADMIN_LEVEL,
  },
  {
    id: 'reporting', label: 'Reporting & exports', description: 'Timesheets, the Time Worked report and exports.', icon: 'BarChart3', minLevel: MIN_TOUR_LEVEL,
  },
  {
    id: 'monitoring', label: 'Monitoring & compliance', description: 'Screenshots, webcam, insider threats and geolocation.', icon: 'Eye', minLevel: ADMIN_LEVEL,
  },
  {
    id: 'roles', label: 'Roles & access', description: 'Custom roles, permissions and the audit trail.', icon: 'KeyRound', minLevel: ADMIN_LEVEL,
  },
];

/**
 * Whether the tour should be offered to this member at all.
 * @param {object} member
 * @returns {boolean}
 */
export function isTourEligible(member) {
  if (!member) return false;
  return getMemberRoleLevel(member) >= MIN_TOUR_LEVEL;
}

/**
 * Steps this member can actually reach within a track, in order. Steps whose
 * screen sits above the member's role level are dropped, so a Team Leader's tour
 * skips admin-only screens (whose data calls are admin-gated) rather than landing
 * there and erroring.
 * @param {object} member
 * @param {string} [tourId='welcome']
 * @returns {Array} accessible steps (empty if not eligible / unknown track)
 */
export function getAccessibleSteps(member, tourId = WELCOME_TOUR_ID) {
  if (!isTourEligible(member)) return [];
  const steps = TOURS[tourId];
  if (!steps) return [];
  const level = getMemberRoleLevel(member);
  return steps.filter((step) => level >= step.minLevel);
}

/**
 * Feature tracks this member can run (each with ≥1 accessible step), in order,
 * for listing in the Help center.
 * @param {object} member
 * @returns {Array<{id:string,label:string,description:string,icon:string}>}
 */
export function getAccessibleTracks(member) {
  if (!isTourEligible(member)) return [];
  return TRACKS.filter((t) => getAccessibleSteps(member, t.id).length > 0);
}

export default TOURS;
